import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProject } from '../api/projects'

// API функции для печати
const getPrintSheets = async (projectId: number) => {
  const response = await fetch(`/api/print/sheets?project_id=${projectId}`)
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to fetch sheets')
  return data.data
}

const createPrintSheet = async (sheetData: any) => {
  const response = await fetch('/api/print/sheets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(sheetData)
  })
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to create sheet')
  return data.data
}

const deletePrintSheet = async (sheetId: number) => {
  const response = await fetch(`/api/print/sheets/${sheetId}`, {
    method: 'DELETE'
  })
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to delete sheet')
  return data
}

const duplicatePrintSheet = async (sheetId: number) => {
  const response = await fetch(`/api/print/sheets/${sheetId}/duplicate`, {
    method: 'POST'
  })
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to duplicate sheet')
  return data.data
}

const exportAllSheetsPDF = async ({ projectId, sheetsData, filename, dpi }: { 
  projectId: number; 
  sheetsData: string[]; 
  filename: string; 
  dpi: number 
}) => {
  const response = await fetch('/api/print/export-all-sheets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ project_id: projectId, sheets_data: sheetsData, filename, dpi })
  })
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to export all sheets')
  return data.data
}



interface PrintSheet {
  id: number
  name: string
  format: string
  orientation: string
  margin_mm: string
  order_index: number
  created_at: string
}

export default function PrintSheetsPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [newSheetData, setNewSheetData] = useState({
    name: '',
    format: 'A4',
    orientation: 'portrait'
  })
  const [isExportAllModalOpen, setIsExportAllModalOpen] = useState(false)
  const [exportAllFilename, setExportAllFilename] = useState('')
  const [exportAllDPI, setExportAllDPI] = useState(150)

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(Number(projectId)),
    enabled: !!projectId,
  })

  const { data: sheets = [], isLoading } = useQuery({
    queryKey: ['print-sheets', projectId],
    queryFn: () => getPrintSheets(Number(projectId!)),
    enabled: !!projectId,
  })

  const createSheetMutation = useMutation({
    mutationFn: createPrintSheet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-sheets', projectId] })
      setIsCreateModalOpen(false)
      setNewSheetData({ name: '', format: 'A4', orientation: 'portrait' })
    },
  })

  const deleteSheetMutation = useMutation({
    mutationFn: deletePrintSheet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-sheets', projectId] })
    },
  })

  const duplicateSheetMutation = useMutation({
    mutationFn: duplicatePrintSheet,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['print-sheets', projectId] })
      // Переходим к редактированию нового лист
      navigate(`/projects/${projectId}/prints/${data.id}`)
    },
  })

  const exportAllSheetsMutation = useMutation({
    mutationFn: exportAllSheetsPDF,
    onSuccess: (data) => {
      console.log('All sheets exported:', data)
      alert('Все листы успешно экспортированы в PDF!')
    },
    onError: (error) => {
      console.error('Export all sheets failed:', error)
      alert('Ошибка экспорта всех листов: ' + error)
    }
  })



  const handleCreateSheet = async () => {
    if (!newSheetData.name.trim()) return
    
    try {
      await createSheetMutation.mutateAsync({
        project_id: Number(projectId),
        ...newSheetData,
        margin_mm: { top: 10, right: 10, bottom: 10, left: 10 }
      })
    } catch (error) {
      console.error('Failed to create sheet:', error)
    }
  }

  const handleDeleteSheet = async (sheetId: number, sheetName: string) => {
    if (!confirm(`Удалить лист "${sheetName}"?`)) return
    
    try {
      await deleteSheetMutation.mutateAsync(sheetId)
    } catch (error) {
      console.error('Failed to delete sheet:', error)
    }
  }

  const handleDuplicateSheet = async (sheetId: number) => {
    try {
      await duplicateSheetMutation.mutateAsync(sheetId)
    } catch (error) {
      console.error('Failed to duplicate sheet:', error)
    }
  }

  const handleExportAllSheets = async () => {
    if (!exportAllFilename.trim() || sheets.length === 0) return
    
    try {
      // For MVP, generate placeholder HTML content for each sheet
      const sheetsData = sheets.map((sheet: PrintSheet, index: number) => {
        return `
          <div class="sheet-content" style="width: 210mm; height: 297mm; margin: 10mm; border: 1px solid #000; page-break-after: always;">
            <h1>Лист ${index + 1}: ${sheet.name}</h1>
            <p>Формат: ${sheet.format}</p>
            <p>Ориентация: ${sheet.orientation}</p>
            <p>Создан: ${new Date(sheet.created_at).toLocaleDateString()}</p>
            <div style="margin-top: 50px;">
              <p>Здесь будет содержимое листа...</p>
              <p>Viewport'ы, схемы, штампы...</p>
            </div>
          </div>
        `
      })
      
      await exportAllSheetsMutation.mutateAsync({
        projectId: Number(projectId),
        sheetsData,
        filename: exportAllFilename.trim(),
        dpi: exportAllDPI
      })
      
      setIsExportAllModalOpen(false)
      setExportAllFilename('')
      setExportAllDPI(150)
      
    } catch (error) {
      console.error('Failed to export all sheets:', error)
    }
  }



  if (isLoading) {
    return <div className="text-center py-8">Загрузка листов...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Link to={`/projects/${projectId}`} className="text-blue-600 hover:text-blue-800 mb-2 inline-block">
            ← Назад к проекту
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Печать схем</h1>
          <p className="text-gray-600 mt-1">
            {project?.name} - Управление листами для печати
          </p>
        </div>
        <div className="flex space-x-3">
          <Link
            to={`/projects/${projectId}/pdf-files`}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm font-medium"
          >
            📁 PDF файлы
          </Link>
          <button
            onClick={() => {
              if (sheets.length === 0) {
                alert('Нет листов для экспорта')
                return
              }
              setExportAllFilename(`all_sheets_${project?.name || 'project'}_${new Date().toISOString().split('T')[0]}`)
              setIsExportAllModalOpen(true)
            }}
            disabled={sheets.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            📄 Экспорт всех листов
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            ➕ Создать лист
          </button>
        </div>
      </div>

      {/* Sheets List */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Листы для печати ({sheets.length})
          </h2>
          <p className="text-gray-600 mt-1">
            Создавайте и настраивайте листы для печати схем проекта
          </p>
        </div>

        {sheets.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 text-6xl mb-4">🖨️</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Нет листов для печати</h3>
            <p className="text-gray-600 mb-4">
              Создайте первый лист для начала работы с печатью схем
            </p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Создать первый лист
            </button>
          </div>
        ) : (
          <div className="p-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {sheets.map((sheet: PrintSheet, index: number) => (
                <div key={sheet.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">#{index + 1}</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        sheet.orientation === 'portrait' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {sheet.format} {sheet.orientation === 'portrait' ? 'Книжная' : 'Альбомная'}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleDuplicateSheet(sheet.id)}
                        disabled={duplicateSheetMutation.isPending}
                        className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50"
                        title="Дублировать лист"
                      >
                        📋
                      </button>
                      <button
                        onClick={() => handleDeleteSheet(sheet.id, sheet.name)}
                        disabled={deleteSheetMutation.isPending}
                        className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                        title="Удалить лист"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  
                  <h3 className="font-medium text-gray-900 mb-2">{sheet.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Создан: {new Date(sheet.created_at).toLocaleDateString('ru-RU')}
                  </p>
                  
                  <div className="flex gap-2">
                    <Link
                      to={`/projects/${projectId}/prints/${sheet.id}`}
                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 text-center transition-colors"
                    >
                      ✏️ Редактировать
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create Sheet Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Создать новый лист</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название листа
                </label>
                <input
                  type="text"
                  value={newSheetData.name}
                  onChange={(e) => setNewSheetData({ ...newSheetData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Например: Лист 1 - Главная схема"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Формат
                </label>
                <select
                  value={newSheetData.format}
                  onChange={(e) => setNewSheetData({ ...newSheetData, format: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="A4">A4</option>
                  <option value="A3">A3</option>
                  <option value="A2">A2</option>
                  <option value="A1">A1</option>
                  <option value="A0">A0</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ориентация
                </label>
                <select
                  value={newSheetData.orientation}
                  onChange={(e) => setNewSheetData({ ...newSheetData, orientation: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="portrait">Книжная</option>
                  <option value="landscape">Альбомная</option>
                </select>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleCreateSheet}
                disabled={createSheetMutation.isPending || !newSheetData.name.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {createSheetMutation.isPending ? 'Создание...' : 'Создать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export All Sheets Modal */}
      {isExportAllModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Экспорт всех листов</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Имя файла
                </label>
                <input
                  type="text"
                  value={exportAllFilename}
                  onChange={(e) => setExportAllFilename(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="all_sheets_project_2025-01-25"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Качество DPI
                </label>
                <select
                  value={exportAllDPI}
                  onChange={(e) => setExportAllDPI(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={72}>72 DPI (Низкое)</option>
                  <option value={150}>150 DPI (Среднее)</option>
                  <option value={300}>300 DPI (Высокое)</option>
                </select>
              </div>
              
              <div className="text-sm text-gray-600">
                Будет экспортировано {sheets.length} листов в один PDF файл
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setIsExportAllModalOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleExportAllSheets}
                disabled={exportAllSheetsMutation.isPending || !exportAllFilename.trim()}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {exportAllSheetsMutation.isPending ? '🔄 Экспорт...' : 'Экспортировать'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
